import { useNavigate } from '@tanstack/react-router';
import { ArrowUpRight } from 'lucide-react';
import { MemberAvatar } from '@/components/MemberAvatar';
import { ParentChip, type NoteRecord } from '@/components/NoteCard';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { projectAccent } from '@/lib/deterministic-color';
import { cn } from '@/lib/utils';

const fmtTimeAgo = (minutes: number): string => {
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${Math.round(minutes)}m ago`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}h ago`;
  const days = Math.round(minutes / (60 * 24));
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.round(days / 7)}w ago`;
  return `${Math.round(days / 30)}mo ago`;
};

export interface NotePreviewPanelProps {
  workspaceSlug: string;
  /** When non-null, the panel renders open. `null` = closed. The
   *  caller controls open state by setting / clearing this. */
  note: NoteRecord | null;
  onClose: () => void;
}

/**
 * Notes redesign — slide-in side preview. Mirrors the prototype's
 * `.gp-panel` quick-look: parent indicator + status dot in the
 * header, title + meta grid + body excerpt in the body. The
 * "Open" CTA navigates to `NoteDetailPage` for editing — preserves
 * the read-only quick-look gesture without regressing edit access
 * (Q2 hybrid).
 *
 * The body is rendered as HTML via `dangerouslySetInnerHTML` because
 * the API stores rich-text bodies (TipTap output) and the panel just
 * displays them. Editing happens on `NoteDetailPage`.
 */
export const NotePreviewPanel = ({
  workspaceSlug,
  note,
  onClose,
}: NotePreviewPanelProps) => {
  const navigate = useNavigate();

  return (
    <Sheet
      open={note !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent side="right" className="flex flex-col gap-0 sm:max-w-[460px]">
        {note ? (
          <>
            <SheetHeader
              className={cn(
                'flex flex-row items-center gap-2.5 border-b px-[18px] py-3.5',
                'border-[var(--glass-card-border)]',
              )}
            >
              <span
                className={cn(
                  'rounded font-mono text-[11px] font-medium uppercase tracking-[0.06em] text-foreground-muted',
                  'bg-[var(--surface-quiet,var(--secondary))] px-1.5 py-px',
                )}
              >
                NOTE
              </span>
              <span className="text-foreground-muted inline-flex items-center gap-1.5 text-[11px]">
                <span
                  aria-hidden="true"
                  className={cn(
                    'size-[7px] rounded-full',
                  )}
                  style={{ background: projectAccent(note.railSeed) }}
                />
                <span className="truncate">
                  {note.parent.kind === 'task'
                    ? note.parent.taskTitle
                    : note.parent.projectName}
                </span>
              </span>
              {/* Title + description hidden visually — Sheet primitive
                  needs them for a11y but we render our own header. */}
              <SheetTitle className="sr-only">{note.title || 'Untitled note'}</SheetTitle>
              <SheetDescription className="sr-only">
                Note preview. Open the full note for editing.
              </SheetDescription>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto p-[18px]">
              <h2
                className={cn(
                  'mb-3.5 text-[22px] font-semibold leading-[1.25] tracking-[-0.02em]',
                  note.title.trim().length === 0
                    ? 'text-foreground-faint italic'
                    : 'text-foreground',
                )}
              >
                {note.title.trim().length === 0 ? 'Untitled' : note.title}
              </h2>

              <dl
                className={cn(
                  'grid grid-cols-[88px_1fr] gap-x-4 gap-y-2 border-y py-3.5 text-[12.5px]',
                  'border-[var(--glass-card-border)]',
                )}
              >
                <dt className="text-foreground-faint py-1">Pinned to</dt>
                <dd className="m-0 flex flex-wrap items-center gap-1.5 py-1">
                  <ParentChip note={note} />
                </dd>
                <dt className="text-foreground-faint py-1">Author</dt>
                <dd className="text-foreground m-0 flex items-center gap-1.5 py-1">
                  <MemberAvatar
                    id={note.authorId}
                    name={note.authorName}
                    email={note.authorEmail}
                    size="xs"
                    className="!size-5 !text-[8px]"
                  />
                  <span className="truncate">
                    {note.authorName ?? note.authorEmail ?? 'Unknown'}
                  </span>
                </dd>
                <dt className="text-foreground-faint py-1">
                  {note.edited ? 'Edited' : 'Created'}
                </dt>
                <dd className="text-foreground m-0 py-1 font-mono text-[12px] tabular-nums">
                  {fmtTimeAgo(note.recencyMinutes)}
                </dd>
              </dl>

              <div className="mt-[18px]">
                <h3
                  className={cn(
                    'text-foreground-faint mb-2 text-[11px] font-semibold uppercase tracking-[0.06em]',
                  )}
                >
                  Content
                </h3>
                {note.body.trim().length === 0 ? (
                  <p className="text-foreground-faint text-[13px] italic leading-[1.6]">
                    No content yet.
                  </p>
                ) : (
                  <div
                    className={cn(
                      'text-foreground-muted text-[13px] leading-[1.6]',
                      // Prose-like rendering for TipTap HTML output.
                      // Same selector pattern used by NoteEditor.
                      '[&>p]:my-3 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0',
                      '[&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-[18px] [&_h1]:font-semibold',
                      '[&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-[16px] [&_h2]:font-semibold',
                      '[&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:text-[14px] [&_h3]:font-semibold',
                      '[&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-5',
                      '[&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-5',
                      '[&_li]:my-1',
                      '[&_code]:bg-secondary [&_code]:rounded [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.88em]',
                      '[&_blockquote]:my-3 [&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:text-foreground-faint',
                      '[&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2',
                    )}
                    dangerouslySetInnerHTML={{ __html: note.body }}
                  />
                )}
              </div>
            </div>

            {/* Footer CTA — navigate to the full note for editing.
                Mirrors the prototype's slide-in-then-jump gesture
                (preview here, edit on its own page). */}
            <div
              className={cn(
                'flex flex-shrink-0 items-center justify-between gap-2 border-t px-[18px] py-3',
                'border-[var(--glass-card-border)]',
              )}
            >
              <span className="text-foreground-faint text-[11px]">
                Press <kbd className="bg-secondary rounded border px-1 py-px font-mono text-[10px]">Esc</kbd> to close
              </span>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  onClose();
                  void navigate({
                    to: '/w/$workspaceSlug/n/$noteUlid',
                    params: { workspaceSlug, noteUlid: note.id },
                  });
                }}
              >
                Open
                <ArrowUpRight className="size-3.5" />
              </Button>
            </div>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
};
